import type {Price} from '@/redux/v1/models';

import {useBreakpointUp} from '@travel-frontend/core';
import {IconArrowForward} from '@travel-frontend/icons';
import {
  Button,
  HeadlineLarge,
  Paper,
  ParagraphLarge,
  LabelSmallSemibold,
  LabelMediumBold,
  ParagraphMedium,
  ParagraphXSmall,
} from '@travel-frontend/ui-components';
import {useTranslation, Trans} from 'react-i18next';

import illustration from '@/assets/images/cross-border-illustration-v1.svg';
import {Container} from '@/components/CrossBorderBannerV1/components/Container';
import {Illustration} from '@/components/CrossBorderBannerV1/components/Illustration';
import {InnerLayout} from '@/components/CrossBorderBannerV1/components/InnerLayout';
import {InviaLogo} from '@/components/CrossBorderBannerV1/components/InviaLogo';
import {OuterLayout} from '@/components/CrossBorderBannerV1/components/OuterLayout';

type Props = React.ComponentPropsWithoutRef<typeof Container> & {
  url: string;
  airports: Array<string>;
  price: Price;
  priceEnabled: boolean;
};

export const CrossBorderBannerV1 = ({
  url,
  airports,
  price,
  priceEnabled,
  ...restProps
}: Props) => {
  const {t} = useTranslation('crossBorder');

  const mdUp = useBreakpointUp('md');
  const lgUp = useBreakpointUp('lg');
  // eslint-disable-next-line sonarjs/no-nested-conditional -- really?
  const size = lgUp ? 'large' : mdUp ? 'medium' : 'small';
  const Title = size === 'small' ? LabelSmallSemibold : HeadlineLarge;
  const TitleTeaser = size === 'small' ? LabelMediumBold : HeadlineLarge;
  const Subtitle = {
    small: ParagraphXSmall,
    medium: ParagraphMedium,
    large: ParagraphLarge,
  }[size];

  return (
    <Container {...restProps} href={url} target="_blank" rel="noreferrer">
      <Paper>
        <OuterLayout size={size}>
          <div style={{textAlign: 'center'}}>
            <Illustration src={illustration} size={size} alt="" />
          </div>
          <InnerLayout size={size}>
            <Title gridColumn={size === 'medium' ? 'span 2' : undefined}>
              <TitleTeaser as="span">{t('teaser') + ' '}</TitleTeaser>
              {t('title', {airports})}
            </Title>
            <Subtitle color="contentSecondary">
              <Trans
                ns="crossBorder"
                i18nKey="subtitle"
                components={{
                  partner: <InviaLogo />,
                }}
              />
            </Subtitle>
            <Button
              as="span"
              variant="interaction"
              icon={<IconArrowForward />}
              iconPosition="right"
              style={{backgroundColor: '#05AFF0'}}
            >
              {t('cta', {
                context: priceEnabled ? 'with_price' : 'without_price',
                price: price.amount,
                currency: price.currency,
              })}
            </Button>
          </InnerLayout>
        </OuterLayout>
      </Paper>
    </Container>
  );
};
